import type {
  AgentApiJobChangeProcessBoardsItem,
  AgentApiJobChangeProcessBoardsShow,
  AgentApiJobChangeProcessesRecord,
} from '@/types/serializers';
import { useQueries, useQueryClient, type QueryObserverResult } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import useFetchJobChangeProcessBoards from './useFetchJobChangeProcessBoards';
import type { JobChangeProcessBoardsRequest } from '../types/JobChangeProcessBoard';

export const buildBoardQueryParams = (query?: JobChangeProcessBoardsRequest): string => {
  if (!query) return '';

  const params: string[] = [];

  if (query.page !== undefined) {
    params.push(`page=${query.page}`);
  }

  if (query.keyword) {
    params.push(`search_query[keyword]=${encodeURIComponent(query.keyword)}`);
  }

  if (query.organization_user_ids && query.organization_user_ids.length > 0) {
    query.organization_user_ids.forEach((id) => {
      params.push(`search_query[organization_user_ids][]=${id}`);
    });
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
};

export const mergeRecords = (
  loaded: AgentApiJobChangeProcessesRecord[],
  fetched: AgentApiJobChangeProcessesRecord[]
): AgentApiJobChangeProcessesRecord[] => {
  const seen = new Set(loaded.map((record) => record.id));
  return [...loaded, ...fetched.filter((record) => !seen.has(record.id))];
};

export type BoardPageState = {
  hasNextPage: boolean;
  isFetching: boolean;
};

// 未取得の列を詰めると添字がずれて構造共有が効かず、D&D の楽観更新が古いデータで上書きされる
const combineBoardResults = (
  results: QueryObserverResult<AgentApiJobChangeProcessBoardsShow>[]
) => ({
  boardsData: results.map((result) => result.data),
  fetchingStates: results.map((result) => result.isFetching),
});

export default function useFetchJobChangeProcessBoardsDetails(
  initialQuery?: JobChangeProcessBoardsRequest
) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<JobChangeProcessBoardsRequest>(initialQuery ?? {});
  // ページはボードごとに持つ。共通化すると1カラムのページ送りが全カラムに波及する
  const [pages, setPages] = useState<Record<number, number>>({});
  // queryFn は再取得時に最新のページを見る必要があるため、state と別に ref でも保持する
  const pagesRef = useRef<Record<number, number>>({});
  const [versions, setVersions] = useState<Record<number, number>>({});
  const accumulatedRef = useRef<Record<number, AgentApiJobChangeProcessesRecord[]>>({});

  const {
    data: boardsList,
    refetch: boardsListRefetch,
    isFetched: isBoardsListFetched,
  } = useFetchJobChangeProcessBoards();

  const boardRecords: AgentApiJobChangeProcessBoardsItem[] = boardsList?.records ?? [];
  const boardIds = boardRecords.map((board) => board.id);
  const filterKey = buildBoardQueryParams({ ...filters, page: undefined });

  const { boardsData, fetchingStates } = useQueries({
    // filters と pages は filterKey / pages[id] として queryKey に含めている。
    // ref は「取得のたびに参照が変わり再取得が止まらなくなる」ため意図的に queryKey から外す
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queries: boardIds.map((id) => ({
      queryKey: ['jobChangeProcessBoard', id, filterKey, pages[id] ?? 1, versions[id] ?? 0],
      queryFn: async () => {
        const page = pagesRef.current[id] ?? 1;
        const response = await fetch(
          `/agent/api/job_change_process_boards/${id}.json${buildBoardQueryParams({ ...filters, page })}`
        );
        if (!response.ok) throw new Error(`ボードの取得に失敗しました (id=${id})`);
        const data: AgentApiJobChangeProcessBoardsShow = await response.json();

        accumulatedRef.current[id] =
          page === 1 ? data.records : mergeRecords(accumulatedRef.current[id] ?? [], data.records);

        return { ...data, records: accumulatedRef.current[id] };
      },
    })),
    combine: combineBoardResults,
  });

  // 「読み込み済み件数 < 総件数」で判定する。ページ番号から算出するとページ送りが先行したときに取りこぼす
  const boardStates: Record<number, BoardPageState> = {};
  boardIds.forEach((id, index) => {
    const data = boardsData[index];
    boardStates[id] = {
      hasNextPage: data ? data.records.length < data.total_count : false,
      isFetching: fetchingStates[index] ?? false,
    };
  });

  const loadMoreBoard = useCallback(
    (boardId: number) => {
      const state = boardStates[boardId];
      if (!state?.hasNextPage || state.isFetching) return;
      pagesRef.current = { ...pagesRef.current, [boardId]: (pagesRef.current[boardId] ?? 1) + 1 };
      setPages(pagesRef.current);
    },
    [boardStates]
  );

  const resetPages = useCallback(() => {
    accumulatedRef.current = {};
    pagesRef.current = {};
    setPages({});
  }, []);

  const reloadBoards = (ids: number[]) => {
    ids.forEach((id) => delete accumulatedRef.current[id]);
    pagesRef.current = Object.fromEntries(
      Object.entries(pagesRef.current).filter(([id]) => !ids.includes(Number(id)))
    );
    setPages(pagesRef.current);
    setVersions((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, (prev[id] ?? 0) + 1])),
    }));
  };

  const boardsRefetch = useCallback(
    async (options?: { resetPages?: boolean }) => {
      if (options?.resetPages) resetPages();
      const { data } = await boardsListRefetch();
      if (options?.resetPages) {
        await queryClient.invalidateQueries({ queryKey: ['jobChangeProcessBoard'] });
      }
      return { data: { records: data?.records ?? [] } };
    },
    [boardsListRefetch, resetPages, queryClient]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<JobChangeProcessBoardsRequest>) => {
      resetPages();
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [resetPages]
  );

  const setKeyword = useCallback((keyword: string) => updateFilters({ keyword }), [updateFilters]);

  const setOrganizationUserIds = useCallback(
    (organization_user_ids: number[]) => updateFilters({ organization_user_ids }),
    [updateFilters]
  );

  return {
    boards: { records: boardRecords },
    boardsData,
    boardStates,
    boardsRefetch,
    reloadBoards,
    loadMoreBoard,
    resetPages,
    isBoardsListFetched,
    filters,
    filterKey,
    setKeyword,
    setOrganizationUserIds,
  };
}
